import React from "react";
import { useNavigate } from "react-router-dom";
import "./HomePage.css";
import { Row, Col, Button, Typography, Spin } from "antd";
import { Box } from "@mui/material";
import {
  EnvironmentOutlined,
  FolderOpenOutlined,
  RestOutlined,
  FileProtectOutlined,
  ContainerOutlined
} from "@ant-design/icons";
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// Custom Hooks
import useAuth from "../../hooks/useAuth";

// 🔑 อิมพอร์ตคลังสิทธิ์กลาง
import {
  ROLE_ADMIN,
  ROLE_REVENUE,
  SURVEY_NAV_ROLES
} from '../../constants/roles';

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // 🛡️ ฟังก์ชันช่วยเช็คสิทธิ์แบบ Array (ปลอดภัย รองรับทั้ง String และ Number)
  const userRoles = Array.isArray(user?.roles)
    ? user.roles.map(String)
    : [String(user?.roles || '')];

  const hasAccess = (targetRoles) => userRoles.some(role => targetRoles.includes(role));

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', width: '100%' }}>
        <Spin size="large" tip="กำลังยืนยันสิทธิ์ผู้ใช้งานระบบ..." style={{ color: '#ffffff' }} />
      </Box>
    );
  }

  return (
    <Box
      id="sso-homepage-root"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // justify: 'center',       // 🎯 ดึงเนื้อหาจัดวางตรงกลางแนวตั้ง
        minHeight: '100vh',      // 🎯 ยืดความสูงกล่องให้เต็ม 100% ของความสูงหน้าจอจริง
        width: '100%',
        boxSizing: 'border-box',
        gap: 5,
        py: 4
      }}
    >
      {/* โซนหัวข้อประจำกอง */}
      <Row justify="center" style={{ width: '100%' }}>
        <Col span={24} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography style={{ fontWeight: "900", color: "#ffffff", fontSize: "2.8rem", marginBottom: '4px' }}>
            ระบบ Single Sign-On
          </Typography>
          <Typography style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "1.3rem", fontWeight: 400 }}>
            กองสารสนเทศภาษีและทะเบียนทรัพย์สิน เทศบาลนครสุราษฎร์ธานี
          </Typography>
        </Col>
      </Row>

      {/* แผงรันปุ่มเมนู 6 ปุ่ม */}
      <Box sx={{ width: '100%', maxWidth: 1300, px: 2 }}>
        <Row gutter={[24, 24]} justify="center" className="row-button">

          {/* เมนูที่ 1: จัดการชื่อผู้ใช้งาน (เฉพาะ Admin: '100') */}
          {hasAccess(ROLE_ADMIN) && (
            <Col xs={24} sm={12} md={8} lg={4}>
              <Button
                type="primary"
                style={{ backgroundColor: "#4caf50", borderColor: "#388e3c", width: '100%' }}
                onClick={() => navigate("/usermanage")}
                className="size-button"
                icon={<ManageAccountsIcon />}
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span>User Management</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 300 }}>(จัดการชื่อผู้ใช้งาน)</span>
                </div>
              </Button>
            </Col>
          )}

          {/* เมนูที่ 2: แผนที่ภูมิสารสนเทศ (เปิดให้เข้าดูได้ทุกกลุ่ม) */}
          <Col xs={24} sm={12} md={8} lg={4}>
            <Button
              type="primary"
              style={{ backgroundColor: "#e5b217", borderColor: "#e5b217", width: '100%' }}
              onClick={() => { window.location.href = "/map"; }}
              className="size-button"
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <EnvironmentOutlined style={{ fontSize: 18, marginBottom: 4 }} />
                <span>Map</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 300 }}>(แผนที่ภูมิสารสนเทศ)</span>
              </div>
            </Button>
          </Col>

          {/* เมนูที่ 3: ทะเบียนคุมแปลงที่ดิน */}
          {hasAccess(SURVEY_NAV_ROLES) && (
            <Col xs={24} sm={12} md={8} lg={4}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "#00a8cc",
                  borderColor: "#00a8cc",
                  width: '100%'
                }}
                onClick={() => navigate("/landregistration")}
                className="size-button"
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <FileProtectOutlined style={{ fontSize: 20, marginBottom: 4 }} />
                  <span>Taxmap Registration</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 300 }}>(ทะเบียนคุมแปลงที่ดิน)</span>
                </div>
              </Button>
            </Col>
          )}

          {/* เมนูที่ 4: ซองเอกสารออนไลน์ */}
          {hasAccess(SURVEY_NAV_ROLES) && (
            <Col xs={24} sm={12} md={8} lg={4}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "#ff6f59",
                  borderColor: "#ff6f59",
                  width: '100%'
                }}
                onClick={() => navigate("/taxmapfolder")}
                className="size-button"
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <ContainerOutlined style={{ fontSize: 20, marginBottom: 4 }} />
                  <span>Taxmap Folders</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 300 }}>(ซองเอกสารออนไลน์)</span>
                </div>
              </Button>
            </Col>
          )}

          {/* เมนูที่ 5: ทะเบียนคุมซองรับเอกสาร */}
          {hasAccess(SURVEY_NAV_ROLES) && (
            <Col xs={24} sm={12} md={8} lg={4}>
              <Button
                type="primary"
                style={{ backgroundColor: "#d5791f", borderColor: "#d5791f", width: '100%' }}
                onClick={() => navigate("/trackdoc/index")}
                className="size-button"
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <FolderOpenOutlined style={{ fontSize: 18, marginBottom: 4 }} />
                  <span>Track Documents</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 300 }}>(ทะเบียนคุมซอง)</span>
                </div>
              </Button>
            </Col>
          )}

          {/* เมนูที่ 6: ค่าธรรมเนียมการเก็บขยะ */}
          {hasAccess([...ROLE_ADMIN, ...ROLE_REVENUE]) && (
            <Col xs={24} sm={12} md={8} lg={4}>
              <Button
                type="primary"
                style={{ backgroundColor: "#791c89", borderColor: "#791c89", width: '100%' }}
                onClick={() => navigate("/wastefee")}
                className="size-button"
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <RestOutlined style={{ fontSize: 18, marginBottom: 4 }} />
                  <span>Waste Collection Fee</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 300 }}>(ค่าธรรมเนียมเก็บขยะ)</span>
                </div>
              </Button>
            </Col>
          )}
        </Row>
      </Box>
    </Box>
  );
}